import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Color, Container, FillGradient, Sprite, Text, TextStyle } from "pixi.js";
import { Group, Tween } from "tweedle.js";
import { languageGlobal } from "../config/GlobalVariableConfig";
import { getFontConfig } from "../config/introTextConfig";
import { GAME_LAYOUT_HEIGHT, GAME_LAYOUT_WIDTH } from "../config/reelConfig";

declare const window: any;
const translationManager = window.hacksawCasino.Translation;
const TILE_BASE_SPINE_CONFIG = {
    skeleton: "introTicketBaseSkel",
    atlas: "introTicketBaseAtlas",
    animation: "frame"
};

// Map tile image names to their Spine skeleton/atlas keys
const TILE_SPINE_CONFIG: Record<string, { skeleton: string; atlas: string; animation: string }> = {
    image1: { skeleton: "introImage1Skel", atlas: "introImage1Atlas", animation: "wild_ticket" },
    image2: { skeleton: "introImage2Skel", atlas: "introImage2Atlas", animation: "scatter_ticket" },
    image3: { skeleton: "introImage3Skel", atlas: "introImage3Atlas", animation: "max_win_ticket" },
};

export class IntroScene extends Container {

    private allTextContainer: Container = new Container();
    private volatilityContainer: Container = new Container();
    private continueButtonContainer: Container = new Container();

    private tileContainer1 = new Container();
    private tileContainer2 = new Container();
    private tileContainer3 = new Container();
    private tileIndex: number = 0;
    private animationInterval: any;

    private tweenGroupForTiles: Group = new Group();
    private tweenGroupForScale: Group = new Group();
    private tweenGroupForBreath: Group = new Group();

    // Track all tile spine instances for lifecycle management
    private tileSpineInstances: Spine[] = [];

    private logo = Sprite.from('introLogoSprite');
    private logoContainer = new Container();
    private backgroundImage: Sprite;

    private screenWidth: number = 0;
    private screenHeight: number = 0;

    constructor() {
        super();

        this.eventMode = 'static';
        this.interactive = true;

        console.log("languageGlobal", languageGlobal);

        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    override destroy(): void {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);

        // Stop all spine animations before destroying
        this.tileSpineInstances.forEach(spine => {
            spine.state.clearTracks();
        });
        this.tileSpineInstances = [];

        super.destroy({ children: true });
    }

    private handleVisibilityChange = (): void => {
        if (document.hidden) {
            this.pauseTileSpineAnimations();
            this.endTileAnimation();
        } else {
            this.resumeTileSpineAnimations();
            const isLandscape = this.screenWidth > this.screenHeight;
            if (!isLandscape) {
                this.startTileAnimation();
            }
        }
    };

    private pauseTileSpineAnimations(): void {
        this.tileSpineInstances.forEach(spine => {
            spine.state.timeScale = 0;
        });
    }

    private resumeTileSpineAnimations(): void {
        this.tileSpineInstances.forEach(spine => {
            spine.state.timeScale = 1;
        });
    }

    public async showInitLogo(screenWidth: number, screenHeight: number) {

        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        this.backgroundImage = Sprite.from('intro_bg');
        this.addChild(this.backgroundImage);
        this.addChild(this.allTextContainer);

        this.logoContainer.addChild(this.logo);
        this.logoContainer.scale = 0.80;
        this.allTextContainer.addChild(this.logoContainer);

        this.setupVolatilityText();

        // Tile 1
        this.allTextContainer.addChild(this.tileContainer1);
        this.setupTile(
            this.tileContainer1,
            "image1",
            translationManager.game('intro_ticket_heading_1_uc'),
            translationManager.game('intro_ticket_desc_1')
        );

        // Tile 2
        this.allTextContainer.addChild(this.tileContainer2);
        this.setupTile(
            this.tileContainer2,
            "image2",
            translationManager.game('intro_ticket_heading_2_uc'),
            translationManager.game('intro_ticket_desc_2')
        );

        // Tile 3
        this.allTextContainer.addChild(this.tileContainer3);
        this.setupTile(
            this.tileContainer3,
            "image3",
            translationManager.game('intro_ticket_heading_3_uc'),
            translationManager.game('intro_ticket_desc_3')
        );

        this.setupContinueButton();

        let currentVal: number = .75;
        new Tween({ x: currentVal }, this.tweenGroupForScale)
            .to({ x: 1 }, 300)
            .onComplete(() => {
                this.tweenGroupForScale.removeAll();
            })
            .onUpdate((object) => {
                currentVal = object.x;
                this.resize(this.screenWidth, this.screenHeight, currentVal);
            })
            .start();
    }

    private setupTile(
        tileContainer: Container,
        tileImageName: string,
        headingTextVal: string,
        descTextVal: string
    ): void {

        // const tileBase: Sprite = Sprite.from("textBase");
        // tileBase.anchor = 0.5;
        // tileBase.scale = 1.1;
        // tileContainer.addChild(tileBase);

        const spineConfig = TILE_SPINE_CONFIG[tileImageName];
        const tileBaseSpine = Spine.from({
            skeleton: TILE_BASE_SPINE_CONFIG.skeleton,
            atlas: TILE_BASE_SPINE_CONFIG.atlas,
        });
        tileBaseSpine.state.setAnimation(0, TILE_BASE_SPINE_CONFIG.animation, true);
        tileBaseSpine.scale.set(1.1);
        tileContainer.addChild(tileBaseSpine);
        this.tileSpineInstances.push(tileBaseSpine);


        if (spineConfig) {
            const tileSpine = Spine.from({
                skeleton: spineConfig.skeleton,
                atlas: spineConfig.atlas,

            });

            let spineScale = 1;
            let spineY = 0;


            if (tileImageName === "image1") {
                spineY = 0;
            } else if (tileImageName === "image2") {
                spineY = 40
            } else if (tileImageName === "image3") {
                spineY = 15;
            }

            tileSpine.scale.set(spineScale);
            tileSpine.position.set(0, spineY);

            tileSpine.state.setAnimation(0, spineConfig.animation, true);

            tileContainer.addChild(tileSpine);

            this.tileSpineInstances.push(tileSpine);
        } else {
            // Fallback if spine assets aren't found
            console.log(`No Spine config found for tile: ${tileImageName}. Falling back to Sprite.`);
            const fallbackSprite = Sprite.from(tileImageName);
            fallbackSprite.anchor.set(0.5);
            fallbackSprite.scale.set(0.9);
            tileContainer.addChild(fallbackSprite);
        }
        // ─────────────────────────────────────────────────────────────────────

        const headingY = -200;
        const descY = 170;

        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);
        const colors = ["#f5b504", "#fffff2", "#f7c714"].map(
            (color) => Color.shared.setValue(color).toNumber()
        );
        colors.forEach((number, index) => {
            fill.addColorStop(index / colors.length, number);
        });

        const {
            fontFamily: fontFamilyHeading,
            fontWeight: fontWeightHeading,
            fontSize: fontSizeHeading,
            letterSpacing: letterSpacingHeading,
            lineHeight: lineHeightHeading,
        } = getFontConfig(languageGlobal, "heading");

        const headingText = new Text({
            text: headingTextVal,
            style: {
                fontFamily: fontFamilyHeading,
                fill: { fill },
                fontWeight: fontWeightHeading,
                fontSize: fontSizeHeading,
                dropShadow: {
                    blur: 5,
                    color: "#000000",
                    distance: 6
                },
                letterSpacing: letterSpacingHeading,
                wordWrap: true,
                wordWrapWidth: 420,
                align: "center",
                lineHeight: lineHeightHeading,
                breakWords: true,
            },
        });

        headingText.anchor.set(0.5, 0.5);
        headingText.position.set(0, headingY);
        tileContainer.addChild(headingText);

        const {
            fontFamily: fontFamilyDesc,
            fontWeight: fontWeightDesc,
            fontSize: fontSizeDesc,
            letterSpacing: letterSpacingDesc,
            lineHeight: lineHeightDesc,
        } = getFontConfig(languageGlobal, "description");

        const descriptionTextStyle = new TextStyle({
            fontFamily: fontFamilyDesc,
            fill: "#ffffff",
            fontWeight: fontWeightDesc,
            fontSize: fontSizeDesc,
            align: "center",
            wordWrap: true,
            letterSpacing: letterSpacingDesc,
            ...(lineHeightDesc != null && { lineHeight: lineHeightDesc }),
            breakWords: true,
        });

        const descriptionText = new Text({
            text: descTextVal,
            style: descriptionTextStyle,
        });

        descriptionText.style.wordWrapWidth = 400;

        ["zh-cn", "de-de", "id-id", "cs-cz"].forEach((lang) => {
            if (languageGlobal === lang) {
                if (tileImageName === "image3") {
                    descriptionText.style.fontSize = 30;
                }
            }
        })

        if (languageGlobal === "zh-tw") {
            descriptionText.style.fontSize = 28;
        }

        if (languageGlobal === "ko-kr") {
            descriptionText.style.fontSize = 27;
        }
        if (languageGlobal === "ja-jp") {
            if (tileImageName === "image1") {
                descriptionText.style.fontSize = 23;
            } else {
                descriptionText.style.fontSize = 21;
            }
        }

        descriptionText.anchor.set(0.5, 0.5);
        descriptionText.position.set(0, descY);
        tileContainer.addChild(descriptionText);
    }

    public update() {
        this.tweenGroupForScale.update();
        this.tweenGroupForTiles.update();
        this.tweenGroupForBreath.update();

        this.tileSpineInstances.forEach(spine => {
            spine.update(0);
        });
    }

    private setupVolatilityText(): void {

        this.volatilityContainer = new Container();

        const volatilityBase: Sprite = Sprite.from('volatilityBase');
        volatilityBase.anchor = 0.5;
        this.volatilityContainer.addChild(volatilityBase);



        this.allTextContainer.addChild(this.volatilityContainer);

        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);
        const colors = ["#f5b504", "#fffff2", "#f7c714"].map((color) => Color.shared.setValue(color).toNumber());

        colors.forEach((number, index) => {
            const ratio = index / colors.length;

            fill.addColorStop(ratio, number);
        });

        const {
            fontFamily: fontFamilyVal,
            fontWeight: fontWeightVal,
            fontSize: fontSizeVal,
        } = getFontConfig(languageGlobal, "volatility");


        const text = new Text({
            text: translationManager.game('intro_volatility_uc'),//'VOLATILITY',
            style: {
                fontFamily: fontFamilyVal,
                fill: { fill },
                stroke: {
                    color: "#ebcb31",
                    width: 1,
                },
                dropShadow:
                {
                    blur: 5,
                    color: "#2e00b1",
                    distance: 5,
                },
                fontSize: fontSizeVal,
                fontWeight: fontWeightVal,
            }
        });



        text.anchor.set(0.5, 0.5);
        text.position.set(-100, 103);

        const textScale = Math.min(220 / text.width, 1);
        text.scale = textScale;

        this.volatilityContainer.addChild(text);

        // volatilityBase.position.set(20 + text.width / 2, -2);
        volatilityBase.position.set(0, 100);


        // Define the volatility value
        const volatilityVal = 3; // Example value

        // Adding 5 vol images in a line
        const volSpacing = 42; // Space between each vol
        const volStartX = Math.min(35, -55 + text.width / 2); // Starting position to center vols

        for (let i = 0; i < 5; i++) {
            const volTexture = i < volatilityVal ? 'volatilityOn' : 'volatilityOff';
            const vol = Sprite.from(volTexture);
            vol.scale = 0.9;
            vol.anchor.set(0.5);
            vol.position.set(volStartX + i * volSpacing, 100); // Adjust Y position as needed
            this.volatilityContainer.addChild(vol);
        }

    }

    private setupContinueButton(): void {


        this.continueButtonContainer = new Container();
        // this.continueButtonContainer.cursor = 'pointer';
        // this.continueButtonContainer.eventMode = 'static';
        // this.continueButtonContainer.interactive = true;

        // const continueButtonBase: Sprite = Sprite.from('volatilityBase');
        // continueButtonBase.anchor = 0.5;
        // continueButtonBase.position.set(0, 2);

        // // this.continueButtonContainer.pivot.set(this.continueButtonContainer.width / 2, this.continueButtonContainer.height / 2)
        // this.continueButtonContainer.addChild(continueButtonBase);

        // this.continueButtonContainer.position.set(1500, 2300);
        this.allTextContainer.addChild(this.continueButtonContainer);

        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);
        const colors = ["#f5b504", "#fffff2", "#f7c714"].map((color) => Color.shared.setValue(color).toNumber());

        colors.forEach((number, index) => {
            const ratio = index / colors.length;

            fill.addColorStop(ratio, number);
        });

        const {
            fontFamily: fontFamilyVal,
            fontWeight: fontWeightVal,
            fontSize: fontSizeVal,
        } = getFontConfig(languageGlobal, "continue");


        const text = new Text({
            text: translationManager.game('intro_continue_uc'),//'Click to Continue',
            style: {
                fontFamily: fontFamilyVal,
                fill: { fill },
                dropShadow:
                {
                    blur: 1,
                    color: "#000000",
                    distance: 6,
                    // width: 12,
                },
                fontWeight: fontWeightVal,
                fontSize: fontSizeVal,
            }
        });


        text.anchor = 0.5;

        const currentScale = Math.min(440 / text.width, 1);
        text.scale = currentScale;

        text.position.set(0, 5);
        // text.position.set(continueButtonBase.width / 2, continueButtonBase.height / 2);

        this.continueButtonContainer.addChild(text);


        const tweenBreath = new Tween(text.scale, this.tweenGroupForBreath)
            .to({ x: currentScale * 1.03, y: currentScale * 1.03 }, 1000) // Scale up
            .yoyo(true) // Automatically reverses the tween
            .repeat(Infinity) // Infinite loop
            .onComplete(() => {
                this.tweenGroupForBreath.remove(tweenBreath);
            })
            .start();

        // this.continueButtonContainer.on('pointerover', () => {
        //     this.continueButtonContainer.tint = "#ffaa00";
        // });

        // this.continueButtonContainer.on('pointerout', () => {
        //     this.continueButtonContainer.tint = "#ffffff";
        // });

    }

    public resize(screenWidth: number, screenHeight: number, zoomFactor?: number): void {

        // console.log(`resize screenWidth${screenWidth} screenHeight ${screenHeight}`);

        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        const isLandscape = screenWidth > screenHeight;

        let portraitscale;
        if (!isLandscape) {
            this.startTileAnimation();
            portraitscale = 1.75;

            this.logoContainer.position.set(780, 460);

            this.continueButtonContainer.scale = 1;
            this.continueButtonContainer.position.set(GAME_LAYOUT_WIDTH / 2, 1499);

            this.volatilityContainer.position.set(GAME_LAYOUT_WIDTH / 2, 774);
            this.tileContainer1.scale.set(0.8);
            this.tileContainer2.scale.set(0.8);
            this.tileContainer3.scale.set(0.8);

        } else {
            this.landscapeTilePositions();
            this.endTileAnimation();

            portraitscale = 1;

            this.logoContainer.position.set(780, 434);

            this.continueButtonContainer.scale = 1;
            this.continueButtonContainer.position.set(GAME_LAYOUT_WIDTH / 2, 1504);

            this.volatilityContainer.position.set(GAME_LAYOUT_WIDTH / 2, 739);

        }


        const scale = portraitscale * (zoomFactor ? zoomFactor : 1) * Math.min(screenWidth / (isLandscape ? 1920 : 1080), screenHeight / (isLandscape ? 1080 : 1920));
        let bgScale;
        const BG_SCALE_FACTOR = Math.max(screenWidth / GAME_LAYOUT_HEIGHT, screenHeight / GAME_LAYOUT_HEIGHT);

        this.allTextContainer.scale.set(scale, scale);
        const xPos = (GAME_LAYOUT_WIDTH * scale - this.screenWidth) / 2;
        const yPos = (GAME_LAYOUT_HEIGHT * scale - this.screenHeight) / 2;
        this.allTextContainer.x = -xPos;
        this.allTextContainer.y = -yPos;

        bgScale = Math.max(scale, BG_SCALE_FACTOR);
        this.backgroundImage.scale.set(bgScale, bgScale)
        const bgXPos = (GAME_LAYOUT_WIDTH * bgScale - screenWidth) / 2;
        const bgYPos = (GAME_LAYOUT_HEIGHT * bgScale - screenHeight) / 2;
        this.backgroundImage.position.set(-bgXPos, -bgYPos);





    }

    public endTileAnimation(): void {
        clearInterval(this.animationInterval);
        this.isAnimatingTiles = false;
    }

    private landscapeTilePositions(): void {

        // this.tileIndex = 0;
        clearInterval(this.animationInterval);

        // Cancel any active tweens (assuming Tween.js has a way to cancel)
        this.tweenGroupForTiles.removeAll();

        this.tileContainer1.scale.set(0.9);
        this.tileContainer2.scale.set(0.9);
        this.tileContainer3.scale.set(0.9);
        this.tileContainer1.position.set(394, 1124);
        this.tileContainer2.position.set(GAME_LAYOUT_WIDTH / 2, 1124); // Place off-screen to the right
        this.tileContainer3.position.set(1654, 1124); // Place off-screen to the right
    }

    private isAnimatingTiles = false;

    private startTileAnimation(): void {
        if (this.isAnimatingTiles) return;
        this.isAnimatingTiles = true;

        this.resetTilePositions();
        clearInterval(this.animationInterval);

        this.animationInterval = setInterval(() => {
            this.cycleTiles();
        }, 3000);
    }

    private resetTilePositions(): void {
        this.tileContainer1.position.set(GAME_LAYOUT_WIDTH / 2, 1149);
        this.tileContainer2.position.set(GAME_LAYOUT_WIDTH, 1149); // Place off-screen to the right
        this.tileContainer3.position.set(GAME_LAYOUT_WIDTH, 1149); // Place off-screen to the right
        this.tileIndex = 0;
        clearInterval(this.animationInterval);
    }

    private cycleTiles(): void {
        const currentTile = [this.tileContainer1, this.tileContainer2, this.tileContainer3][this.tileIndex];
        const nextTile = [this.tileContainer1, this.tileContainer2, this.tileContainer3][(this.tileIndex + 1) % 3];

        new Tween(currentTile.position, this.tweenGroupForTiles)
            .to({ x: 0 }, 500)
            .start();

        new Tween(nextTile.position, this.tweenGroupForTiles)
            .to({ x: GAME_LAYOUT_WIDTH / 2 }, 500)
            .onComplete(() => {
                // Move currentTile to the far right after it exits the screen
                currentTile.position.set(GAME_LAYOUT_WIDTH, 1149);
            })
            .start();

        this.tileIndex = (this.tileIndex + 1) % 3;
    }

}
